import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/navbar";
import Footer from "./footer";

function SubscriptionAgreement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen overflow-hidden">
        {/* Header Section with gradient background */}
        <section className="relative bg-black text-white py-32 overflow-hidden">
          <div className="absolute inset-0">
            {/* Dot Pattern */}
            <div
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, #333 1px, transparent 0)`,
                backgroundSize: "24px 24px",
              }}
            />
            {/* Center Radial Gradient */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.15) 0%, rgba(0, 0, 0, 0) 100%)",
              }}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="container mx-auto px-4 relative z-10"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-center mb-6">
              Subscription Agreement
            </h1>
            <p className="text-xl text-center text-gray-400">
              Lexi.Tips Pty Ltd – Subscription Agreement
              <br />
              Effective Date: 01 April 2025
            </p>
          </motion.div>
        </section>

        {/* Content Section */}
        <section className="relative py-24 bg-white">
          <div className="absolute inset-0">
            {/* Dot Pattern */}
            <div
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, #e5e5e5 1px, transparent 0)`,
                backgroundSize: "24px 24px",
              }}
            />
            {/* Center Radial Gradient */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
              }}
            />
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="max-w-4xl mx-auto space-y-12">
              {/* Nature of the Service Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">1. Nature of the Service</h2>
                <p className="text-gray-600 leading-relaxed">
                  1.1 Lexi.Tips provides an AI-powered analytical tool that
                  processes horse racing data to generate daily tipping
                  suggestions. This service is available via a weekly
                  subscription.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  1.2 Lexi.Tips does not provide or facilitate any betting
                  services, nor does it encourage or promote gambling. All tips
                  and analytical insights are general in nature and intended for
                  informational and entertainment purposes only.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  1.3 Lexi.Tips does not consider your personal financial
                  objectives, circumstances, or needs when generating its
                  analytical content. You should consider whether the
                  information is appropriate for your individual situation and
                  seek independent advice if necessary.
                </p>
              </motion.div>

              {/* Service Use and Access Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">
                  2. Service Use and Access
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  2.1 Upon payment of the applicable subscription fees,
                  Lexi.Tips grants you a non-exclusive, non-transferable,
                  revocable licence to access and use the Service for your
                  personal use.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  2.2 Lexi.Tips uses statistical models and AI technologies to
                  analyse racing data and provide daily tips. The Service does
                  not guarantee outcomes or performance, and past performance is
                  not indicative of future results.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  2.3 Lexi.Tips reserves the right to modify, suspend or enhance
                  features of the Service at any time.
                </p>
              </motion.div>

              {/* Your Responsibilities Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">3. Your Responsibilities</h2>
                <p className="text-gray-600 leading-relaxed">
                  3.1 You are responsible for maintaining the confidentiality of
                  your account login credentials and for all activity conducted
                  through your account.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  3.2 You agree not to reproduce, distribute, or resell any
                  content or tips provided via the Service without express
                  written permission from Lexi.Tips.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  3.3 You agree not to rely solely on our Service for making
                  financial decisions or placing bets and acknowledge that the
                  Service is not a substitute for independent judgment.
                </p>
              </motion.div>

              {/* Disclaimers and Limitations Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">
                  4. Disclaimers and Limitations
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  4.1 The Service is provided "as is" and "as available". We do
                  not guarantee uninterrupted access or error-free performance.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  4.2 Lexi.Tips makes no warranties, express or implied, as to
                  the accuracy or reliability of the tips provided. No guarantee
                  is made that any tips will result in financial gain.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  4.3 To the maximum extent permitted by law, Lexi.Tips
                  disclaims any liability for loss, damage, or expense arising
                  from use of or reliance on the Service.
                </p>
              </motion.div>

              {/* Subscription, Fees and Cancellation Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">
                  5. Subscription, Fees and Cancellation
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  5.1 Subscription fees are billed weekly and are non-refundable
                  except as required by Australian Consumer Law.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  5.2 You may cancel your subscription at any time. Cancellation
                  will take effect at the end of the current billing cycle.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  5.3 Lexi.Tips reserves the right to adjust pricing and will
                  notify subscribers in advance of any changes. Continued use of
                  the Service after such changes constitutes acceptance.
                </p>
              </motion.div>

              {/* Intellectual Property Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">6. Intellectual Property</h2>
                <p className="text-gray-600 leading-relaxed">
                  6.1 All content, data, models, and software used in the
                  Service are the intellectual property of Lexi.Tips or its
                  licensors.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  6.2 You may not copy, modify, reverse engineer, or distribute
                  the Service or its outputs without prior written consent.
                </p>
              </motion.div>

              {/* Privacy and Data Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">7. Privacy and Data</h2>
                <p className="text-gray-600 leading-relaxed">
                  7.1 We collect and process your data in accordance with our
                  Privacy Policy, available at{" "}
                  <a
                    href="https://Lexi.Tips/privacy"
                    className="text-brand-pink hover:underline"
                  >
                    https://Lexi.Tips/privacy
                  </a>
                  .
                </p>
                <p className="text-gray-600 leading-relaxed">
                  7.2 You consent to Lexi.Tips using anonymised data to improve
                  its services and analytics.
                </p>
              </motion.div>

              {/* Termination Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">8. Termination</h2>
                <p className="text-gray-600 leading-relaxed">
                  8.1 Lexi.Tips may suspend or terminate access to the Service
                  if you breach this Agreement.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  8.2 Upon termination, your access to the Service and any
                  stored account data will cease. We may retain data for backup
                  and compliance purposes.
                </p>
              </motion.div>

              {/* General Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">9. General</h2>
                <p className="text-gray-600 leading-relaxed">
                  9.1 This Agreement is governed by the laws of Queensland,
                  Australia.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  9.2 Lexi.Tips may update this Agreement from time to time.
                  Continued use after updates indicates your acceptance.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  9.3 If you have any questions, contact us at{" "}
                  <a
                    href="mailto:support@lexi.tips"
                    className="text-brand-pink hover:underline"
                  >
                    support@lexi.tips
                  </a>
                  .
                </p>
              </motion.div>

              {/* Agreement Acceptance Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="bg-black text-white p-8 rounded-xl relative overflow-hidden"
              >
                <div className="absolute inset-0 opacity-50">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 1px 1px, #333 1px, transparent 0)`,
                      backgroundSize: "24px 24px",
                    }}
                  />
                </div>
                <div className="relative z-10">
                  <p className="text-gray-400 text-center">
                    By subscribing to the Service, you confirm that you have
                    read and agree to this Subscription Agreement.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default SubscriptionAgreement;
